import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  Input,
  OnInit,
} from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import {
  PatientTargetDataModel,
  PatientTargetModel,
  PatientTargetStatus,
  PromptCodeModel,
  PromptScheduleModel,
} from '../../../core/api/models';
import {
  getPatientTargetStatusColor,
  getPatientTargetStatusLabel,
} from '../../../shared/utils/utils';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { AlertService } from '../../../shared/services/alert.service';
@Component({
  selector: 'app-box-echoic',
  templateUrl: './box-echoic.component.html',
  styleUrls: ['../data-entry-feedback.css'],
  standalone: false,
})
export class BoxEchoicComponent extends BaseBoxComponent {
  text: string | undefined = '-';
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  requiredTrials: number = 0;
  targetID: number = 0;
  targetStatus: PatientTargetStatus = PatientTargetStatus.Ready;
  @Input() options: PromptCodeModel[] = [];
  currentPromptCodeID?: number;
  @Input() promptSchedule?: PromptScheduleModel | null;
  @Input() allPromptCodes: PromptCodeModel[] = [];

  @Input() target: PatientTargetModel | undefined = undefined;
  @Input() isPreview?: boolean;

  rates: { name: string; icon: string; color: string }[] = [
    {
      name: 'Articulation',
      icon: '0',
      color: '#5DADE2',
    },
    {
      name: 'Volume',
      icon: '.5',
      color: '#5DADE2',
    },
    {
      name: 'Speed',
      icon: 'NA',
      color: '#5DADE2',
    },
    {
      name: 'Tone',
      icon: '1',
      color: '#5DADE2',
    },
    {
      name: 'Rhythm',
      icon: '1',
      color: '#5DADE2',
    },
  ];

  selectedOption: PromptCodeModel | null = null;
  justSelectedOption: any;
  selectedRate: any;
  isPlayingVideo: boolean = false;
  totalSuccess: number = 0;
  selectedOptions: (PromptCodeModel & {
    RecordedDate?: string;
    FormattedDate?: string;
    Rate?: { name: string; icon: string; color: string };
  })[] = [];
  targetData: PatientTargetDataModel[] = [];
  remainingTrials: number = 0;
  addingTargetData: boolean = false;
  /** Prompt trial waiting for a rate. A rate tap updates this row instead of inserting another. */
  private pendingTrial: {
    recordedDate: string;
    promptId: number;
    promptText: string;
  } | null = null;
  /** Option ID that was just recorded (success flash); cleared after 220ms. */
  lastRecordedOptionId: number | null = null;
  /** Option ID showing cooldown bar; cleared when buffer ends. */
  cooldownOptionId: number | null = null;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(
        this.targetID
      );
      if (targetData) {
        this.targetData = targetData.filter((t) => !t.DeletedDate);

        this.remainingTrials =
          (this.target?.DesiredTrials ?? 0) - this.targetData.length;
        if (this.remainingTrials < 0) {
          this.remainingTrials = 0;
        }
        //Add the options inside targetData to the selectedOptions based on the options available in the options array
        this.selectedOptions = this.targetData
          .map((t) => {
            const source = this.allPromptCodes.find((o) => o.ID === t.IntValue);
            if (!source) {
              return undefined;
            }
            const option: PromptCodeModel & {
              RecordedDate?: string;
              FormattedDate?: string;
              Rate?: { name: string; icon: string; color: string };
            } = {
              ...source,
              RecordedDate: t.RecordedDate ?? '',
              FormattedDate: this.formatDate(new Date(t.RecordedDate ?? '')),
            };
            if (t.TextValue?.includes('|')) {
              const rateName = t.TextValue.split('|')[1];
              option.Rate = this.rates.find((r) => r.name === rateName);
            }
            return option;
          })
          .filter((o) => o !== undefined) as (PromptCodeModel & {
          RecordedDate?: string;
          FormattedDate?: string;
          Rate?: { name: string; icon: string; color: string };
        })[];

        this.totalSuccess = this.selectedOptions.filter(
          (o) => o.IsIndependent
        ).length;
        if (this.selectedOptions.length > 0) {
          this.selectedOption =
            this.selectedOptions[this.selectedOptions.length - 1];
        } else {
          this.selectedOption = null;
        }
      } else {
        this.targetData = [];
        this.selectedOptions = [];
        this.totalSuccess = 0;
        this.selectedOption = null;
      }
    });
  }

  ngOnInit(): void {
    this.text = this.target?.TargetName ?? '-';
    this.targetID = this.target?.ID ?? 0;
    this.targetStatus = this.target?.TargetStatus ?? PatientTargetStatus.Ready;
    this.requiredTrials = this.target?.DesiredTrials ?? 0;
    this.currentPromptCodeID = this.target?.CurrentPromptCodeID ?? 0;

    if (!this.currentPromptCodeID) {
      this.currentPromptCodeID = this.options[0].ID ?? 0;
    }
  }

  override showExtra(): void {
    if (this.extra2Opened) {
      return;
    }

    this.justSelectedOption = null;
    this.pendingTrial = null;
    //this.selectedOption = null;
    this.selectedRate = null;
    this.isPlayingVideo = false;
    this.isRecording = false;
    super.showExtra();
  }

  selectOption(option: PromptCodeModel) {
    if (!this.selectedOptions) {
      this.selectedOptions = [];
    }

    if (!this.saveOption(option)) {
      return;
    }

    this.selectedOption = option;
    this.justSelectedOption = option;
    setTimeout(() => {
      if (!this.extraOpened || !this.mainBox || !this.extraBox) {
        return;
      }
      this.updateExtraContainerPosition(
        this.mainBox.nativeElement,
        this.extraBox.nativeElement
      );
    });
  }

  selectRate(rate: { name: string }) {
    this.selectedRate = rate;
    const pending = this.pendingTrial;
    if (!pending) {
      return;
    }

    const existing = this.targetData.find(
      (t) => t.RecordedDate === pending.recordedDate
    );
    if (!existing) {
      return;
    }

    this.patientSessionService.saveTargetData({
      ...existing,
      IntValue: pending.promptId,
      TextValue: pending.promptText + '|' + rate.name,
      PatientTargetID: this.targetID,
      PatientSessionID: existing.PatientSessionID ?? 0,
      RecordedDate: pending.recordedDate,
    });
  }

  saveOption(option: PromptCodeModel): boolean {
    return this.addSessionValue(option);
  }

  addSessionValue(option: PromptCodeModel): boolean {
    if (this.addingTargetData) {
      return false;
    }
    this.addingTargetData = true;
    //get date with up to seconds, remove milisencods
    const recordedDate = new Date()
      .toISOString()
      .slice(0, 19)
      .replace('T', ' ');
    const dateToSave = new Date(recordedDate).fromUTCtoServerString();

    //check if the dateToSave is already in the targetData
    const isDateInTargetData = this.targetData.some(
      (t) => t.RecordedDate === dateToSave
    );
    if (isDateInTargetData) {
      this.addingTargetData = false;
      return false;
    }

    this.pendingTrial = {
      recordedDate: dateToSave,
      promptId: option.ID ?? 0,
      promptText: option.Text ?? '',
    };
    this.patientSessionService.addTargetData({
      TextValue: option.Text,
      IntValue: option.ID,
      PatientTargetID: this.targetID,
      PatientSessionID: 0,
      RecordedDate: dateToSave,
    });
    this.lastRecordedOptionId = option.ID ?? null;
    this.cooldownOptionId = option.ID ?? null;
    setTimeout(() => {
      this.lastRecordedOptionId = null;
    }, 220);
    setTimeout(() => {
      this.addingTargetData = false;
      this.cooldownOptionId = null;
    }, 1000);
    return true;
  }

  private videoStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  isRecording: boolean = false;

  record() {
    this.isRecording = true;
    this.selectedRate = {
      name: 'Record',
      icon: 'R',
      color: '#5DADE2',
    };
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          this.videoStream = stream;
          const videoElement = document.querySelector(
            'video#recordingVideo'
          ) as HTMLVideoElement;
          if (videoElement) {
            videoElement.srcObject = stream;
            videoElement.play();
          }

          this.mediaRecorder = new MediaRecorder(stream);
          this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
            if (event.data.size > 0) {
              this.recordedChunks.push(event.data);
            }
          };

          this.mediaRecorder.onstop = () => {
            const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
            if (this.selectedRate) {
              this.selectedRate.videoBlob = blob;
              this.selectedRate.videoUrl = URL.createObjectURL(blob);
            } else {
              console.error('No selected option to attach the video to.');
            }
          };

          this.mediaRecorder.start();
        })
        .catch((error) => {
          console.error('Error accessing media devices.', error);
        });
    } else {
      console.error('getUserMedia not supported on your browser!');
    }
  }

  stopRecording() {
    this.isRecording = false;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.videoStream?.getTracks().forEach((track) => track.stop());
    }
  }

  playVideo() {
    const videoElement = document.querySelector(
      'video#recordedVideo'
    ) as HTMLVideoElement;
    if (videoElement) {
      this.isPlayingVideo = true;
      videoElement.play();
      videoElement.classList.remove('hide');
      videoElement.onended = () => {
        videoElement.classList.add('hide');
        this.isPlayingVideo = false;
      };
    }
  }

  getStatusText() {
    return getPatientTargetStatusLabel(this.targetStatus);
  }

  getStatusColor() {
    return getPatientTargetStatusColor(this.targetStatus);
  }

  getIcon(option: PromptCodeModel): string {
    //get first letter of the text
    return option.Abbreviation ?? '';
  }

  override doLongPress(): void {
    this.showExtra2();
  }

  openExtra2(event: Event) {
    event.stopPropagation();
    this.showExtra2();

    console.log(this.selectedOptions);
  }

  deleteData(date?: string) {
    if (date) {
      //confirm the deletion
      const confirmed = this.alertService.confirm(
        'Are you sure you want to delete this data?',
        'Delete Data'
      );
      confirmed.subscribe((result) => {
        if (result) {
          let targetDataToSave: PatientTargetDataModel | undefined =
            this.targetData.find((t) => t.RecordedDate === date);

          if (targetDataToSave) {
            targetDataToSave.DeletedDate = new Date().toServerString();
            this.patientSessionService.saveTargetData(targetDataToSave);
          }
        }
      });
    }
  }

  clickedBox() {
    if (this.extra2Opened) {
      return;
    }
    this.showExtra();
  }
}
